import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Card, { CardBody, CardTitle } from '../../../components/bootstrap/Card';
import Badge from '../../../components/bootstrap/Badge';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../components/bootstrap/Modal';
import Icon from '../../../components/icon/Icon';
import Select from '../../../components/bootstrap/forms/Select';
import Option from '../../../components/bootstrap/Option';
import Button from '../../../components/bootstrap/Button';
import api, { uploadFile, getFileUrl } from '../../../helpers/axios';
import MediaRenderer from '../../../components/MediaRenderer';
import PostTypeIndicator from '../../../components/PostTypeIndicator';
import SubArticleModal from './SubArticleModal';
import { useArticleTypes, ArticleType } from '../../../hooks/useArticleTypes';
import { useCategorySelection, useCategoryHierarchy } from '../../../hooks/useCategories';
import CategorySelector from '../../../components/CategorySelector';
import RegionalSelector from '../../../components/RegionalSelector';
import { useRegionalSelection } from '../../../hooks/useRegional';
import { categoryService, Category } from '../../../services/categoryService';
import { ArticlePayload, CategoryPayload } from '../../../types/articleTypes';
import { RegionalTargetingPayload } from '../../../types/regionalTypes';
import { PostType } from '../../../types/postTypes';
import { usePostTypeSystem } from '../../../hooks/usePostTypes';
import PostTypeSelector from '../../../components/PostTypeSelector';
import EnhancedFileUpload from '../../../components/EnhancedFileUpload';
import { usePincodeSelection } from '../../../hooks/usePincodeSelection';
import { PincodeSelector } from '../../../components/PincodeSearch';
import { PincodeTargetingPayload, PincodeDetails } from '../../../types/pincodeTypes';
import RichTextInput from '../../../components/bootstrap/forms/RichTextInput';
import { useContentEncoding } from '../../../hooks/useContentEncoding';
import { webSeriesService } from '../../../services/webSeriesService';
import { WebSeriesMaster, WebSeriesEpisode } from '../../../types/webSeriesTypes';
import AIAssistant from '../../../components/AIAssistant/AIAssistant';
import { StructuredContentResponse } from '../../../types/aiTypes';
import showNotification from '../../../components/extras/showNotification';

interface ITag {
  text: string;
  color: string;
}

interface ISubArticle {
  id: number;
  title: string;
  body: string;
  fileUrl: string; // Changed from imagePath to fileUrl for unified file handling
  thumbnailPath: string; 
  tags: ITag[] | null; 
  sequence?: number; 
  postType?: PostType; // Add post type support for sub-articles
  articleTypeId?: number; // Article type ID for sub-article
  audioUrl?: string; // TTS-generated audio URL for sub-article narration
  showTitle?: boolean; // Controls whether title is displayed in mobile app
  showThumbnail?: boolean; // Controls whether thumbnail/image is displayed in mobile app
}

interface IArticle {
  id: number;
  title: string;
  description: string;
  body: string;
  imageUrl: string; // Keep as imageUrl for backward compatibility in this component
  tags: ITag[];
  thumbnailPath: string; 
  articleTypeId?: number; 
  articleType?: ArticleType; 
  categories?: Category[]; 
  primaryCategory?: Category; 
  regionalTargeting?: RegionalTargetingPayload; 
  targetStateIds?: number[]; 
  targetStateNames?: string[]; 
  postType?: PostType; // Add post type support
  pincodeTargeting?: PincodeTargetingPayload; // Add pincode targeting support
  isPincodeSpecific?: boolean;
  targetPincodeIds?: number[];
  targetPincodes?: PincodeDetails[]; // Add this field
  audioUrl?: string; // TTS-generated audio URL for article narration
  isAdvertisement?: boolean; // Flag indicating if article is an advertisement/sponsored content
  redirectUrl?: string; // External URL for advertisement redirection
  cardBgColor?: string; // Hex background color for mobile news card
}

/**
 * Strips corrupted S3-prefix from legacy external URLs.
 * Old code accidentally prepended the S3 base URL to YouTube/Vimeo URLs,
 * producing: https://bucket.s3.amazonaws.com/https://youtube.com/...
 */
const cleanExternalUrl = (url: string): string => {
  if (!url) return '';
  // Match https://...amazonaws.com/https://... or https://...amazonaws.com/http://...
  const match = url.match(/^https?:\/\/[^/]+\.amazonaws\.com\/(https?:\/\/.+)$/);
  return match ? match[1] : url;
};

/**
 * Extract YouTube video ID from any YouTube URL format:
 * - youtube.com/watch?v=ID
 * - youtu.be/ID
 * - youtube.com/shorts/ID
 * - youtube.com/embed/ID
 */
const extractYouTubeVideoId = (url: string): string => {
  if (!url) return '';
  const clean = cleanExternalUrl(url);
  // shorts
  const shortsMatch = clean.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return shortsMatch[1];
  // watch?v=
  const watchMatch = clean.match(/[?&]v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) return watchMatch[1];
  // youtu.be/
  const shortMatch = clean.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) return shortMatch[1];
  // embed/
  const embedMatch = clean.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) return embedMatch[1];
  return '';
};

/**
 * Helper function to detect if URL is an external video platform URL
 * Used for showing edit capability in the article editor
 */
const isExternalVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  const externalPatterns = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'dailymotion.com',
    'dai.ly',
    'facebook.com/watch',
    'fb.watch',
    'instagram.com',
    'tiktok.com',
    'twitter.com',
    'x.com'
  ];
  return externalPatterns.some(pattern => lowerUrl.includes(pattern));
};

/**
 * Detect the external platform name from URL
 */
const getExternalPlatformName = (url: string): string => {
  if (!url) return 'External';
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'YouTube';
  if (lowerUrl.includes('vimeo.com')) return 'Vimeo';
  if (lowerUrl.includes('dailymotion.com') || lowerUrl.includes('dai.ly')) return 'Dailymotion';
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.watch')) return 'Facebook';
  if (lowerUrl.includes('instagram.com')) return 'Instagram';
  if (lowerUrl.includes('tiktok.com')) return 'TikTok';
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'Twitter/X';
  return 'External';
};

const ViewArticle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { articleTypes, isLoading: isLoadingArticleTypes, error: articleTypesError } = useArticleTypes();
  
  // Enhanced post type system
  const postTypeSystem = usePostTypeSystem();
  
  // Content encoding hook for encoding/decoding article content
  const { decodeContent, decodeArticlePayload, encodeArticlePayload } = useContentEncoding();
  
  const [article, setArticle] = useState<IArticle | null>(null);
  const [subArticles, setSubArticles] = useState<ISubArticle[]>([]);
  
  // State for editing external URL
  const [isEditingExternalUrl, setIsEditingExternalUrl] = useState(false);
  const [tempExternalUrl, setTempExternalUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editArticle, setEditArticle] = useState<IArticle | null>(null);
  const [isArticleReady, setIsArticleReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [subModalInitialData, setSubModalInitialData] = useState<any>(null);
  const [subSaving, setSubSaving] = useState(false);
  const [useSameAsMainContent, setUseSameAsMainContent] = useState(false);
  
  // Web Series state
  const [webSeriesData, setWebSeriesData] = useState<{
    series: WebSeriesMaster | null;
    episode: WebSeriesEpisode | null;
  } | null>(null);
  const [loadingSeriesData, setLoadingSeriesData] = useState(false);
  
  // AI Assistant state for content generation and voice
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  
  // Initialize category selection with empty values
  const categorySelection = useCategorySelection();
  
  // Initialize regional selection
  const regionalSelection = useRegionalSelection();
  
  // Initialize pincode selection
  const pincodeSelection = usePincodeSelection();
  
  // Track if categories have been initialized to prevent re-initialization
  const [categoriesInitialized, setCategoriesInitialized] = useState(false);
  
  // Track if regional targeting has been initialized
  const [regionalInitialized, setRegionalInitialized] = useState(false);
  
  // Track if pincode targeting has been initialized
  const [pincodeInitialized, setPincodeInitialized] = useState(false);
  
  // Custom hook for category hierarchy (to get all categories for mapping)
  const { categories: allCategories, isLoading: categoriesLoading, error: categoriesError, refetch: categoriesRefetch } = useCategoryHierarchy();

  // State watcher intentionally removed — avoid logging sensitive article content in production.

  // Helper function to convert Category to CategoryPayload format
  const convertToCategoryPayload = (category: Category): CategoryPayload => ({
    categoryId: category.categoryId,
    categoryName: category.categoryName,
    categoryCode: category.categoryCode,
    categoryDescription: category.categoryDescription,
    categoryIcon: category.categoryIcon,
    categoryColor: category.categoryColor,
    displayOrder: category.displayOrder,
    isActive: category.isActive,
    isFeatured: category.isFeatured,
    articleCount: category.articleCount
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setIsArticleReady(false);
      try {
        // Add timeout to detect backend connectivity issues
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Backend connection timeout - please ensure the Spring Boot server is running')), 10000)
        );
        
        const apiCall = Promise.all([
          api.get(`/api/articles/${id}`),
          api.get(`/api/articles/${id}/subarticles`),
        ]);
        
        const [articleRes, subRes] = await Promise.race([apiCall, timeout]) as any;

        // Transform tags into an array of objects if it's a string
        const articleData = articleRes.data;
        
        // Decode article content for secure display
        articleData.title = articleData.title ? decodeContent(articleData.title) : '';
        articleData.description = articleData.description ? decodeContent(articleData.description) : '';
        articleData.body = articleData.body ? decodeContent(articleData.body) : '';
        
        if (typeof articleData.tags === 'string') {
          articleData.tags = articleData.tags.split(',').map((tag: string) => ({ text: tag, color: 'primary' }));
        }

        // Ensure imageUrl is correctly set from fileUrl (enhanced backend field)
        articleData.imageUrl = articleData.fileUrl || articleData.imagePath || '';
        
        // Ensure postType is available for proper media rendering
        articleData.postType = articleData.postType || PostType.NORMAL_POST;

        // Set article type information if available
        if (articleData.articleTypeId && articleTypes.length > 0) {
          articleData.articleType = articleTypes.find(type => type.articleTypeId === articleData.articleTypeId);
        }

        // Article data already contains categories and primaryCategory, no need for separate API call
        // Categories are already included in the article response
        
        setArticle(articleData);
        setEditArticle(articleData);
        
        // Decode sub-articles for display
        const decodedSubArticles = subRes.data.map((sub: any) => ({
          ...sub,
          title: decodeContent(sub.title),
          body: decodeContent(sub.body)
        }));
        
        setSubArticles(decodedSubArticles);
        
        // Reset initialization flags when new article is loaded
        setCategoriesInitialized(false);
        setRegionalInitialized(false);
        setPincodeInitialized(false);
        
        // Mark article as ready for rendering (removed from render condition for debugging)
        setIsArticleReady(true);
        
        // Force RichTextInput re-render with new data
        setRenderKey(prev => prev + 1);
      } catch (err: any) {
        console.error('Article loading error:', err);
        const errorMessage = err.message || 'Failed to load article. Please check if the backend server is running.';
        setError(errorMessage);
        setIsArticleReady(false);
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch if articleTypes are loaded or if we have an error loading them
    if (articleTypes.length > 0 || articleTypesError) {
      fetchData();
    }
  }, [id, articleTypes, articleTypesError]); // Add articleTypesError as dependency

  // Load Web Series data for this article
  useEffect(() => {
    const loadWebSeriesData = async () => {
      if (!article?.id) return;
      
      setLoadingSeriesData(true);
      try {
        const seriesData = await webSeriesService.getSeriesDataForArticle(article.id);
        setWebSeriesData(seriesData);
      } catch (error) {
        console.error('Failed to load Web Series data:', error);
        setWebSeriesData(null);
      } finally {
        setLoadingSeriesData(false);
      }
    };

    loadWebSeriesData();
  }, [article?.id]);

  // Set category selection when article data is loaded
  useEffect(() => {
    
    
    
    
    
    
    
    // Check if we have the exact structure we need
    if (article && article.categories && Array.isArray(article.categories) && article.categories.length > 0 && !categoriesInitialized) {
      
      
      const selectedCategoryIds = article.categories.map(cat => cat.categoryId);
      
      
      
      
      
      // Clear existing selection first
      
      categorySelection.clearSelection();
      
      
      
      // Add each category individually
      selectedCategoryIds.forEach((categoryId, index) => {
        
        categorySelection.toggleCategory(categoryId);
        
      });
      
      // Set primary category
      if (article.primaryCategory?.categoryId) {
        
        categorySelection.setPrimaryCategory(article.primaryCategory.categoryId);
        
      }
      
      
      
      setCategoriesInitialized(true);
    } else {
      console.log('❌ Skipping category initialization:', {
        hasArticle: !!article,
        hasCategories: !!(article?.categories),
        isArray: Array.isArray(article?.categories),
        categoriesLength: article?.categories?.length || 0,
        alreadyInitialized: categoriesInitialized
      });
    }
    
  }, [article?.categories, article?.primaryCategory, categoriesInitialized]);

  // Set regional targeting when article data is loaded
  useEffect(() => {
    
    
    
    
    
    
    
    // Check if we have regional targeting data and it hasn't been initialized yet
    if (article && article.regionalTargeting && !regionalInitialized) {
      
      
      const { regionalTargeting } = article;
      
      
      // Clear existing selection first
      
      regionalSelection.clearSelection();
      
      // Set the regional targeting data
      if (regionalTargeting.targetStateIds && regionalTargeting.targetStateIds.length > 0) {
        
        // Add each state individually using toggleState
        regionalTargeting.targetStateIds.forEach(stateId => {
          regionalSelection.toggleState(stateId);
        });
        
        // Set primary state if available
        if (regionalTargeting.primaryStateId) {
          regionalSelection.setPrimaryState(regionalTargeting.primaryStateId);
        }
      }
      
      if (regionalTargeting.targetScopeId) {
        
        regionalSelection.setTargetScope(regionalTargeting.targetScopeId);
      }
      
      if (regionalTargeting.regionalPriorityId) {
        
        regionalSelection.setRegionalPriority(regionalTargeting.regionalPriorityId);
      }
      
      if (regionalTargeting.languageId) {
        
        regionalSelection.setLanguage(regionalTargeting.languageId);
      }
      
      
      
      setRegionalInitialized(true);
    } else {
      console.log('❌ Skipping regional targeting initialization:', {
        hasArticle: !!article,
        hasRegionalTargeting: !!(article?.regionalTargeting),
        alreadyInitialized: regionalInitialized
      });
    }
    
  }, [article?.regionalTargeting, regionalInitialized]);

  // Set pincode targeting when article data is loaded
  useEffect(() => {
    
    
    
    
    
    
    
    // Check if we have pincode targeting data and it hasn't been initialized yet
    if (article && article.isPincodeSpecific !== undefined && !pincodeInitialized) {
      
      
      // Clear existing selection first
      
      pincodeSelection.clearSelection();
      
      // Set the pincode targeting data
      if (article.isPincodeSpecific) {
        
        pincodeSelection.togglePincodeSpecific(true);
        
        if (article.targetPincodes && article.targetPincodes.length > 0) {
          
          pincodeSelection.setPincodes(article.targetPincodes);
        }
      } else {
        
        pincodeSelection.togglePincodeSpecific(false);
      }
      
      
      
      setPincodeInitialized(true);
    } else {
      console.log('❌ Skipping pincode targeting initialization:', {
        hasArticle: !!article,
        hasPincodeSpecific: article?.isPincodeSpecific !== undefined,
        alreadyInitialized: pincodeInitialized
      });
    }
    
  }, [article?.isPincodeSpecific, article?.targetPincodes, pincodeInitialized]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!editArticle) return;
    
    const { name, value } = e.target;
    
    if (name === 'articleTypeId') {
      const articleTypeId = value ? parseInt(value, 10) : undefined;
      const articleType = articleTypeId ? articleTypes.find(type => type.articleTypeId === articleTypeId) : undefined;
      
      setEditArticle({ 
        ...editArticle, 
        articleTypeId,
        articleType
      });
    } else {
      setEditArticle({ ...editArticle, [name]: value });
    }
  };

  const handleUpdate = async () => {
    if (!editArticle) return;
    
    // Validate required fields
    const errors: string[] = [];
    
    if (!editArticle.title.trim()) {
      errors.push('Title is required');
    }
    
    if (!editArticle.description.trim()) {
      errors.push('Description is required');
    }
    
    if (!editArticle.body.trim()) {
      errors.push('Body is required');
    }
    
    if (!editArticle.articleTypeId) {
      errors.push('Article type is required');
    }

    if (categorySelection.selection.selectedCategories.length === 0) {
      errors.push('At least one category must be selected');
    }

    // Validate regional targeting if configured
    if (!regionalSelection.validateSelection()) {
      errors.push('Please complete the regional targeting configuration (select at least one state, target scope, and priority)');
    }

    // Validate pincode targeting if configured
    if (!pincodeSelection.validateSelection()) {
      errors.push('Please select at least one pincode for pincode-specific articles');
    }

    if (errors.length > 0) {
      showNotification(
        'Validation Error',
        errors.map((e) => `• ${e}`).join('\n'),
        'danger',
      );
      return;
    }
    
    setSaving(true);
    setError(null);
    try {
      // Get selected categories and primary category
      const selectedCategories = categorySelection.getSelectedCategories(allCategories || []);
      const primaryCategory = categorySelection.getPrimaryCategory(allCategories || []);

      // Convert categories to payload format
      const categoriesPayload = selectedCategories.map(convertToCategoryPayload);
      const primaryCategoryPayload = primaryCategory ? convertToCategoryPayload(primaryCategory) : null;

      // Get regional targeting payload
      const regionalPayload = regionalSelection.getRegionalPayload([], [], [], []);

      // Get pincode targeting payload
      const pincodePayload = pincodeSelection.getPayload();

      // Create properly structured ArticlePayload with decoded content first
      const rawPayload: ArticlePayload = {
        articleId: editArticle.id?.toString(),
        title: editArticle.title.trim(),
        description: editArticle.description.trim(),
        body: editArticle.body.trim(),
        tags: editArticle.tags ? editArticle.tags.map(tag => tag.text) : [], // Convert ITag[] to string[]
        fileUrl: editArticle.imageUrl,
        thumbnailPath: editArticle.thumbnailPath,
        postType: editArticle.postType,
        audioUrl: editArticle.audioUrl, // TTS-generated audio URL
        articleTypeId: editArticle.articleTypeId!, // Include article type ID in payload
        isAdvertisement: editArticle.isAdvertisement, // Sponsored content flag
        redirectUrl: editArticle.redirectUrl, // Advertisement redirect URL
        cardBgColor: editArticle.cardBgColor || undefined,
        categories: categoriesPayload,
        primaryCategory: primaryCategoryPayload || undefined,
        regionalTargeting: regionalPayload || undefined,
        targetStateIds: regionalPayload?.targetStateIds,
        targetStateNames: regionalPayload?.targetStateNames,
        pincodeTargeting: pincodePayload || undefined,
        isPincodeSpecific: pincodePayload?.isPincodeSpecific,
        targetPincodeIds: pincodePayload?.targetPincodeIds,
        subArticles: subArticles.map((sub) => ({
          id: sub.id === 0 || sub.id === null ? 0 : sub.id, // Ensure new sub-articles have id 0
          title: sub.title,
          body: sub.body,
          fileUrl: sub.fileUrl, // Changed from imagePath to fileUrl
          thumbnailPath: sub.thumbnailPath,
          tags: sub.tags ? sub.tags.map(tag => tag.text) : [], // Convert ITag[] to string[]
          sequence: sub.sequence || 0, // Include sequence for ordering
          postType: sub.postType || undefined, // Include post type for sub-articles
          articleTypeId: sub.articleTypeId || undefined, // Include article type ID
          audioUrl: sub.audioUrl || undefined, // Include TTS audio URL
          showTitle: sub.showTitle !== undefined ? sub.showTitle : true, // Include visibility control
          showThumbnail: sub.showThumbnail !== undefined ? sub.showThumbnail : true, // Include visibility control
        })),
      };

      const payload = encodeArticlePayload(rawPayload);

      const response = await api.put(`/api/articles/${id}`, payload);
      
      // Decode the response data for display (backend returns encoded content)
      console.log('🔓 Decoding updated article response for display...');
      const updatedArticle = response.data;
      
      // Decode main article content
      updatedArticle.title = updatedArticle.title ? decodeContent(updatedArticle.title) : '';
      updatedArticle.description = updatedArticle.description ? decodeContent(updatedArticle.description) : '';
      updatedArticle.body = updatedArticle.body ? decodeContent(updatedArticle.body) : '';
      
      // Decode sub-articles content
      const decodedSubArticles = (response.data.subArticles || []).map((sub: any) => ({
        ...sub,
        title: decodeContent(sub.title),
        body: decodeContent(sub.body)
      }));
      
      // Update article type information if available
      if (updatedArticle.articleTypeId && articleTypes.length > 0) {
        updatedArticle.articleType = articleTypes.find(type => type.articleTypeId === updatedArticle.articleTypeId);
      }
      
      console.log('✅ Updated article content decoded successfully');
      
      setArticle(updatedArticle);
      setEditArticle(updatedArticle);
      setSubArticles(decodedSubArticles);
      
      // Force RichTextInput re-render with updated data
      setRenderKey(prev => prev + 1);

      showNotification(
        'Article Updated',
        'The article has been updated successfully.',
        'success',
      );
      setShowConfirmUpdate(false);
    } catch (err: any) {
      console.error('Error updating article:', err); // Debugging log
      const message = err.response
        ? 'Failed to update article. Please try again.'
        : 'The backend is processing. Please wait a moment and try again.';
      setError(message);
      showNotification('Update Failed', message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const openSubModal = (sub: ISubArticle) => {
    setSubModalInitialData({
      ...sub,
      tags: sub.tags || [], // Ensure tags is always an array
    });
    setSubModalOpen(true);
  };

  const openNewSubModal = () => {
    setSubModalInitialData({
      id: 0, // Ensure new subarticle ID is 0
      title: '',
      body: '',
      fileUrl: '', // Changed from imagePath to fileUrl
      thumbnailPath: '',
      tags: [],
      postType: undefined, // Add post type support
    });
    setSubModalOpen(true);
  };

  const closeSubModal = () => {
    setSubModalOpen(false);
    setSubModalInitialData(null);
  };

  const handleSaveSubArticle = (updatedSub: ISubArticle) => {
    setSubArticles((prev) => {
      if (updatedSub.id === 0 || updatedSub.id === null) {
        // Ensure id is explicitly set to 0 or null for new subarticles
        return [...prev, { ...updatedSub, id: 0 }];
      }
      return prev.map((s) => (s.id === updatedSub.id ? updatedSub : s));
    });
    closeSubModal();
  };

  const handleReorderSubArticle = (id: number, direction: 'up' | 'down') => {
    setSubArticles((prev) => {
      const index = prev.findIndex((sub) => sub.id === id);
      if (index === -1) return prev;

      const newSubArticles = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;

      if (targetIndex < 0 || targetIndex >= newSubArticles.length) return prev;

      // Swap sequence values
      [newSubArticles[index].sequence, newSubArticles[targetIndex].sequence] =
        [newSubArticles[targetIndex].sequence, newSubArticles[index].sequence];

      // Sort by sequence
      return newSubArticles.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    });
  };

  const handleImageUpload = async (
    file: File,
    field: 'imageUrl' | 'thumbnailPath',
    isSubArticle: boolean = false,
    subArticleId?: number
  ) => {
    try {
      // Pass postType for proper file validation
      const postTypeForUpload = isSubArticle ? undefined : (editArticle?.postType || undefined);
      const fileCategory = field === 'thumbnailPath' ? 'thumbnail' : 'file';
      const uploadedPath = await uploadFile(file, postTypeForUpload, fileCategory);

      if (isSubArticle && subArticleId !== undefined) {
        setSubArticles((prev) =>
          prev.map((sub) =>
            sub.id === subArticleId ? { ...sub, [field]: uploadedPath } : sub
          )
        );
      } else {
        setEditArticle((prev) => {
          if (!prev) return null;
          return { ...prev, [field]: uploadedPath };
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification('Upload Failed', 'Failed to upload file. Please try again.', 'danger');
    }
  };

  const handleRemoveImage = async (
    field: 'imageUrl' | 'thumbnailPath',
    isSubArticle: boolean = false,
    subArticleId?: number
  ) => {
    try {
      if (isSubArticle && subArticleId !== undefined) {
        setSubArticles((prev) =>
          prev.map((sub) =>
            sub.id === subArticleId ? { ...sub, [field]: '' } : sub
          )
        );
      } else {
        setEditArticle((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            [field]: '',
          };
        });
      }
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  // Handle checkbox change for "use same as main content"
  const handleUseSameAsMainContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setUseSameAsMainContent(isChecked);
    
    // If checked and we have a main image, copy it to thumbnail
    if (isChecked && editArticle?.imageUrl) {
      setEditArticle((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          thumbnailPath: prev.imageUrl,
        };
      });
    } else if (!isChecked) {
      // If unchecked, clear the thumbnail (user can upload a different one)
      setEditArticle((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          thumbnailPath: '',
        };
      });
    }
  };

  // Add fallback for broken images
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = '/path/to/placeholder-image.png'; // Replace with a valid placeholder image path
    e.currentTarget.alt = 'Image not available';
  };

  const handleAddTag = (newTag: string) => {
    if (!editArticle || !newTag.trim()) return;
    const tagExists = editArticle.tags.some((tag) => tag.text.toLowerCase() === newTag.toLowerCase());
    if (tagExists) return;
    setEditArticle({
      ...editArticle,
      tags: [...editArticle.tags, { text: newTag, color: 'primary' }],
    });
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (!editArticle) return;
    setEditArticle({
      ...editArticle,
      tags: editArticle.tags.filter((tag) => tag.text !== tagToRemove),
    });
  };

  const handleRemoveSubArticle = (id: number) => {
    setSubArticles((prev) => prev.filter((sub) => sub.id !== id));
  };

  // ================== AI ASSISTANT HANDLERS ==================

  /**
   * Handler for when AI generates structured content
   * This callback receives the full structured response from the AI
   */
  const handleAIContentGenerated = (content: StructuredContentResponse) => {
    console.log('AI Content Generated:', content);
    // Content is handled via onContentSelected callback
  };

  /**
   * Handler for when user selects/applies AI-generated content
   * Updates the article form with the generated content including optional audio
   */
  const handleAIContentSelected = (content: { title: string; description: string; body: string; audioUrl?: string }) => {
    if (!editArticle) return;
    
    setEditArticle((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        title: content.title,
        description: content.description,
        body: content.body,
        ...(content.audioUrl && { audioUrl: content.audioUrl }),
      };
    });
    
    // Force re-render of RichTextInput components with new content
    setRenderKey(prev => prev + 1);
    
    // Show success message with audio status
    if (content.audioUrl) {
      alert('AI-generated content with audio has been applied to your article!');
    } else {
      alert('AI-generated content has been applied to your article!');
    }
    
    // Optionally close the AI assistant
    setShowAIAssistant(false);
  };

  if (loading) return (
    <div className='container py-4'>
      <PageWrapper title='View Article'>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
          <div
            className='spinner-border text-primary'
            style={{ width: '3rem', height: '3rem' }}
            role='status'
          >
            <span className='visually-hidden'>Loading…</span>
          </div>
          <p className='text-muted fs-5 mb-0'>Loading article, please wait…</p>
        </div>
      </PageWrapper>
    </div>
  );
  if (error) return (
    <div className='container py-4'>
      <PageWrapper title='View Article'>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: '12px' }}>
          <span style={{ fontSize: '2.5rem' }}>⚠️</span>
          <p className='text-danger fs-5 mb-0'>{error}</p>
        </div>
      </PageWrapper>
    </div>
  );
  if (!editArticle) return <div className="text-center">No article data available</div>;

  return (
    <div className="container py-4">
      <PageWrapper title="View Article">
        {/* Header Card with AI Assistant Toggle */}
        <Card className="mb-4 shadow-sm">
          <CardBody className="text-center bg-light rounded">
            <div className="d-flex justify-content-between align-items-center">
              <div></div>
              <h2 style={{ fontWeight: 700, letterSpacing: '1px', color: '#2c3e50', margin: 0 }}>
                ✏️ Edit Article
              </h2>
              <div className="d-flex gap-2">
                <Button
                  color="info"
                  onClick={() => navigate(`/admin/articles/${id}/content`)}
                  title="Manage language content for this article"
                >
                  🌐 Language Content
                </Button>
                <Button
                  color={showAIAssistant ? "secondary" : "primary"}
                  onClick={() => setShowAIAssistant(!showAIAssistant)}
                  className="btn-gradient"
                >
                  <i className="fas fa-robot me-2"></i>
                  {showAIAssistant ? 'Hide AI Assistant' : 'AI Assistant'}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
        
        {/* AI Assistant Panel - Full Width when shown */}
        {showAIAssistant && (
          <div className="row mb-4">
            <div className="col-12">
              <AIAssistant
                onContentGenerated={handleAIContentGenerated}
                onContentSelected={handleAIContentSelected}
                currentContent={{
                  title: editArticle?.title || '',
                  description: editArticle?.description || '',
                  body: editArticle?.body || '',
                  postType: editArticle?.postType || undefined
                }}
              />
            </div>
          </div>
        )}
        
        <div className="row">
          {/* Left Column: Article Content (wider) */}
          <div className="col-md-7">
            <Page>
              <div className="row mb-4">
                <div className="col-12">
                  {/* Article Type Display Card */}
                  {editArticle.articleType && (
                    <Card className="mb-4 bg-light">
                      <CardBody>
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h5 className="mb-1">
                              <Badge color="primary" className="me-2">
                                {editArticle.articleType.typeName}
                              </Badge>
                              Article Type
                            </h5>
                            <p className="text-muted mb-0">{editArticle.articleType.typeDescription}</p>
                          </div>
                          <div className="d-flex flex-column align-items-end gap-2">
                            <div className="text-muted">
                              <small>ID: {editArticle.articleType.articleTypeId}</small>
                            </div>
                            <PostTypeIndicator 
                              postType={editArticle.postType} 
                              size="md"
                              showIcon={true}
                              showLabel={true}
                            />
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}
                  
                  {/* Web Series Context Display */}
                  {webSeriesData && (
                    <Card className="mb-4 bg-info-light border-info">
                      <CardBody>
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h5 className="mb-1">
                              <Badge color="info" className="me-2">
                                <i className="fas fa-film me-1"></i>
                                Web Series
                              </Badge>
                              Episode Content
                            </h5>
                            <div className="mb-2">
                              <strong className="text-primary">{webSeriesData.series?.seriesName}</strong>
                              {webSeriesData.series?.description && (
                                <p className="text-muted mb-1 small">{webSeriesData.series.description}</p>
                              )}
                            </div>
                            {webSeriesData.episode && (
                              <div className="d-flex align-items-center gap-3">
                                <span className="badge bg-secondary">
                                  Episode {webSeriesData.episode.episodeNumber}
                                </span>
                                <span className="fw-medium">{webSeriesData.episode.episodeTitle}</span>
                                {webSeriesData.episode.duration && (
                                  <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {webSeriesData.episode.duration} min
                                  </small>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="d-flex flex-column align-items-end gap-2">
                            <div className="text-muted">
                              <small>Series ID: {webSeriesData.series?.seriesId}</small>
                            </div>
                            {webSeriesData.episode && (
                              <div className="text-muted">
                                <small>Episode ID: {webSeriesData.episode.episodeId}</small>
                              </div>
                            )}
                            <Badge 
                              color={webSeriesData.series?.isActive ? 'success' : 'secondary'}
                              className="small"
                            >
                              {webSeriesData.series?.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                        {webSeriesData.episode?.description && (
                          <div className="mt-3 pt-3 border-top">
                            <small className="text-muted">
                              <strong>Episode Description:</strong> {webSeriesData.episode.description}
                            </small>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  )}
                  
                  {loadingSeriesData && (
                    <Card className="mb-4 bg-light border-secondary">
                      <CardBody className="text-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <small className="text-muted">Loading Web Series information...</small>
                      </CardBody>
                    </Card>
                  )}
                  
                  <Card>
                    <CardBody>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Post Type <span className="text-danger">*</span></label>
                        <PostTypeSelector
                          selectedPostType={editArticle.postType || undefined}
                          onPostTypeChange={(postType) => {
                            setEditArticle((prev) => prev ? { ...prev, postType } : prev);
                          }}
                          showPreview={true}
                          showAutoDetect={false}
                          disabled={saving}
                          required={true}
                        />
                      </div>

                      <div className="mb-3">
                        <label>Title</label>
                        {(() => {
                          console.log('🎯 Rendering Title RichTextInput with value:', editArticle.title);
                          return (
                            <RichTextInput
                              key={`title-${id || 'new'}-${renderKey}`}
                              name="title"
                              value={editArticle.title || ''}
                              onChange={(value) => {
                                console.log('Title changed to:', value?.substring(0, 50) + '...');
                                setEditArticle({ ...editArticle, title: value });
                              }}
                              fieldType="title"
                              placeholder="Enter article title..."
                              disabled={saving}
                            />
                          );
                        })()}
                      </div>
                      
                      <div className="mb-3">
                        <label>Article Type</label>
                        {articleTypesError && (
                          <div className="alert alert-danger mb-2" role="alert">
                            <small>{articleTypesError}</small>
                          </div>
                        )}
                        <Select
                          name="articleTypeId"
                          value={editArticle.articleTypeId?.toString() || ''}
                          onChange={handleChange}
                          ariaLabel="Select Article Type"
                          placeholder="Select Article Type"
                          disabled={isLoadingArticleTypes}
                        >
                          {isLoadingArticleTypes ? (
                            <Option value="" disabled>
                              Loading article types...
                            </Option>
                          ) : (
                            <>
                              <Option value="">Select Article Type</Option>
                              {articleTypes.map((type) => (
                                <Option key={type.articleTypeId} value={type.articleTypeId.toString()}>
                                  {type.typeName}
                                </Option>
                              ))}
                            </>
                          )}
                        </Select>
                        {editArticle.articleType && (
                          <small className="text-muted d-block mt-1">
                            {editArticle.articleType.typeDescription}
                          </small>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <label>Categories</label>
                        <CategorySelector
                          selection={categorySelection}
                          showFeatured={true}
                          maxHeight="300px"
                          categories={allCategories}
                          categoriesLoading={categoriesLoading}
                          categoriesError={categoriesError}
                          onCategoriesRetry={categoriesRefetch}
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label>Regional Targeting</label>
                        <RegionalSelector
                          selection={regionalSelection}
                          showQuickSelect={true}
                          maxHeight="300px"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label>Pincode Targeting</label>
                        <PincodeSelector
                          isPincodeSpecific={pincodeSelection.isPincodeSpecific}
                          targetPincodes={pincodeSelection.targetPincodes}
                          onTogglePincodeSpecific={pincodeSelection.togglePincodeSpecific}
                          onPincodeSelectionChange={pincodeSelection.setPincodes}
                          showHelp={true}
                        />
                      </div>
                      
                      {/* Advertisement/Sponsored Content Configuration */}
                      <div className="mb-3 border rounded p-3 bg-light">
                        <label className="fw-semibold">Sponsored Content / Advertisement</label>
                        <div className="mb-2">
                          <small className="text-muted">
                            <i className="fa fa-info-circle me-1"></i>
                            Mark this article as sponsored content to enable click-through redirection.
                          </small>
                        </div>
                        <div className="form-check mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isAdvertisementEdit"
                            checked={editArticle.isAdvertisement || false}
                            onChange={(e) => setEditArticle({ ...editArticle, isAdvertisement: e.target.checked })}
                          />
                          <label className="form-check-label" htmlFor="isAdvertisementEdit">
                            <strong>This is a Sponsored Post / Advertisement</strong>
                            <small className="text-muted d-block">
                              When enabled, tapping this post will redirect users to the external URL below
                            </small>
                          </label>
                        </div>
                        
                        {editArticle.isAdvertisement && (
                          <div className="mb-2">
                            <label className="form-label">Redirect URL <span className="text-danger">*</span></label>
                            <input
                              type="url"
                              className={`form-control ${
                                editArticle.redirectUrl && 
                                !editArticle.redirectUrl.match(/^https?:\/\/[^\s]+$/)
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              placeholder="https://example.com/landing-page"
                              value={editArticle.redirectUrl || ''}
                              onChange={(e) => setEditArticle({ ...editArticle, redirectUrl: e.target.value })}
                            />
                            {editArticle.redirectUrl && !editArticle.redirectUrl.match(/^https?:\/\/[^\s]+$/) && (
                              <div className="invalid-feedback d-block">
                                Please enter a valid URL starting with http:// or https://
                              </div>
                            )}
                            {editArticle.isAdvertisement && !editArticle.redirectUrl && (
                              <div className="text-warning small mt-1">
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                Redirect URL is required for sponsored content
                              </div>
                            )}
                            <small className="text-muted d-block mt-1">
                              <i className="fa fa-external-link me-1"></i>
                              This URL will open in the user's browser when they tap the post
                            </small>
                          </div>
                        )}
                        
                        {editArticle.isAdvertisement && (
                          <div className="alert alert-warning small py-2 mb-0">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            <strong>Advertising Policy:</strong> Ensure redirect URLs comply with advertising policies.
                          </div>
                        )}
                      </div>

                      {/* Card Background Color */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Card Background Color</label>
                        <small className="text-muted d-block mb-2">
                          Sets the card background color in the mobile app. Leave blank for default theme color.
                        </small>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {['#1A1A2E', '#2D3B2D', '#4A1423', '#1C1C1C', '#2C1810', '#1A2744', '#3B2A1A', '#1F3A3A'].map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              title={preset}
                              onClick={() => setEditArticle({ ...editArticle, cardBgColor: preset })}
                              style={{
                                width: 32, height: 32, borderRadius: 6,
                                backgroundColor: preset,
                                border: editArticle.cardBgColor === preset ? '3px solid #0d6efd' : '2px solid #dee2e6',
                                cursor: 'pointer', padding: 0,
                              }}
                            />
                          ))}
                          <input
                            type="color"
                            value={editArticle.cardBgColor || '#1A1A2E'}
                            onChange={(e) => setEditArticle({ ...editArticle, cardBgColor: e.target.value })}
                            style={{ width: 32, height: 32, padding: 2, borderRadius: 6, border: '2px solid #dee2e6', cursor: 'pointer' }}
                          />
                          {editArticle.cardBgColor && (
                            <>
                              <span className="text-muted small font-monospace">{editArticle.cardBgColor}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                onClick={() => setEditArticle({ ...editArticle, cardBgColor: '' })}>
                                Clear
                              </button>
                            </>
                          )}
                        </div>
                        {editArticle.cardBgColor && (
                          <div className="mt-2 p-2 rounded d-flex align-items-center gap-2"
                            style={{ backgroundColor: editArticle.cardBgColor, width: 'fit-content' }}>
                            <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: 13 }}>Bold Title Preview</span>
                            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>body text</span>
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label>Description</label>
                        {(() => {
                          console.log('🎯 Rendering Description RichTextInput with value:', editArticle.description);
                          return (
                            <RichTextInput
                              key={`description-${id || 'new'}-${renderKey}`}
                              name="description"
                              value={editArticle.description || ''}
                              onChange={(value) => {
                                console.log('Description changed to:', value?.substring(0, 50) + '...');
                                setEditArticle({ ...editArticle, description: value });
                              }}
                              fieldType="description"
                              placeholder="Enter article description..."
                              disabled={saving}
                            />
                          );
                        })()}
                      </div>
                      <div className="mb-3">
                        <label>Body</label>
                        {(() => {
                          console.log('🎯 Rendering Body RichTextInput with value:', editArticle.body);
                          return (
                            <RichTextInput
                              key={`body-${id || 'new'}-${renderKey}`}
                              name="body"
                              value={editArticle.body || ''}
                              onChange={(value) => {
                                console.log('Body changed to:', value?.substring(0, 50) + '...');
                                setEditArticle({ ...editArticle, body: value });
                              }}
                              fieldType="body"
                              placeholder="Enter article body content..."
                              disabled={saving}
                            />
                          );
                        })()}
                      </div>
                      <div className="mb-3">
                        <label>Tags</label>
                        <div className="d-flex flex-wrap gap-2">
                          {editArticle.tags && editArticle.tags.length > 0 ? (
                            editArticle.tags.map((tag) => (
                              <Badge
                                key={tag.text}
                                isLight
                                color={tag.color as any}
                                className="d-flex align-items-center gap-2"
                              >
                                {tag.text}
                                <button
                                  type="button"
                                  className="btn-close btn-sm ms-2"
                                  aria-label="Remove"
                                  onClick={() => handleRemoveTag(tag.text)}
                                ></button>
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted">No tags</span>
                          )}
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Add a tag"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.currentTarget.value) {
                                handleAddTag(e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label>File Content</label>
                        <div className="d-flex flex-column align-items-start">
                          {editArticle.imageUrl ? (
                            <div className="d-flex flex-column align-items-start w-100">
                              {/* External URL Editing Mode */}
                              {isEditingExternalUrl && isExternalVideoUrl(editArticle.imageUrl) ? (
                                <div className="w-100 mb-2">
                                  <div className="input-group mb-2">
                                    <span className="input-group-text bg-primary text-white">
                                      <i className="fas fa-link"></i>
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={tempExternalUrl}
                                      onChange={(e) => setTempExternalUrl(e.target.value)}
                                      placeholder="Enter new external video URL (YouTube, Vimeo, etc.)"
                                    />
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-success btn-sm"
                                      onClick={() => {
                                        if (tempExternalUrl.trim()) {
                                          setEditArticle(prev => {
                                            if (!prev) return prev;
                                            return {
                                              ...prev,
                                              imageUrl: tempExternalUrl.trim()
                                            };
                                          });
                                        }
                                        setIsEditingExternalUrl(false);
                                        setTempExternalUrl('');
                                      }}
                                    >
                                      <i className="fas fa-check me-1"></i> Save URL
                                    </button>
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={() => {
                                        setIsEditingExternalUrl(false);
                                        setTempExternalUrl('');
                                      }}
                                    >
                                      <i className="fas fa-times me-1"></i> Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* Show external URL preview for external videos */}
                                  {isExternalVideoUrl(editArticle.imageUrl) ? (
                                    <div className="w-100 mb-2">
                                      {(() => {
                                        const cleanUrl = cleanExternalUrl(editArticle.imageUrl);
                                        const ytVideoId = extractYouTubeVideoId(cleanUrl);
                                        return (
                                          <div className="p-3 bg-light border rounded mb-2">
                                            <div className="d-flex align-items-center mb-2">
                                              <span className="badge bg-primary me-2">
                                                {getExternalPlatformName(cleanUrl)}
                                              </span>
                                              <small className="text-muted">External Video URL</small>
                                            </div>
                                            {/* YouTube Embed Preview — handles watch, youtu.be, shorts */}
                                            {ytVideoId && (
                                              <div className="ratio ratio-16x9 mb-2">
                                                <iframe
                                                  src={`https://www.youtube.com/embed/${ytVideoId}`}
                                                  title="YouTube Video Preview"
                                                  frameBorder="0"
                                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                  allowFullScreen
                                                />
                                              </div>
                                            )}
                                            {cleanUrl.includes('vimeo.com') && (
                                              <div className="ratio ratio-16x9 mb-2">
                                                <iframe
                                                  src={`https://player.vimeo.com/video/${
                                                    cleanUrl.split('vimeo.com/')[1]?.split('/')[0]
                                                  }`}
                                                  title="Vimeo Video Preview"
                                                  frameBorder="0"
                                                  allow="autoplay; fullscreen; picture-in-picture"
                                                  allowFullScreen
                                                />
                                              </div>
                                            )}
                                            <small className="text-muted text-break d-block">{cleanUrl}</small>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  ) : (
                                    <MediaRenderer
                                      fileUrl={editArticle.imageUrl}
                                      postType={editArticle.postType}
                                      alt="File Content"
                                      style={{ 
                                        maxWidth: '100%', 
                                        marginBottom: '10px',
                                        borderRadius: '4px'
                                      }}
                                      onError={(e) => {
                                        console.error('Failed to load content:', editArticle.imageUrl);
                                      }}
                                    />
                                  )}
                                </>
                              )}
                              {/* Action buttons */}
                              {!isEditingExternalUrl && (
                                <div className="d-flex gap-2 mt-2">
                                  {isExternalVideoUrl(editArticle.imageUrl) && (
                                    <button
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => {
                                        setTempExternalUrl(cleanExternalUrl(editArticle.imageUrl));
                                        setIsEditingExternalUrl(true);
                                      }}
                                    >
                                      <i className="fas fa-edit me-1"></i> Edit URL
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleRemoveImage('imageUrl')}
                                  >
                                    <i className="fas fa-trash me-1"></i> Remove File
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <EnhancedFileUpload
                              key={`file-upload-${editArticle.postType || 'normal'}`}
                              instanceId="view-article-main-content"
                              postType={editArticle.postType || PostType.NORMAL_POST}
                              fileCategory="file"
                              onFileUploaded={(path) => {
                                setEditArticle(prev => {
                                  if (!prev) return prev;
                                  const updatedArticle = {
                                    ...prev,
                                    imageUrl: path,
                                    fileUrl: path // Update both fields for compatibility
                                  };
                                  
                                  // If "use same as main content" is checked, also set thumbnail
                                  if (useSameAsMainContent) {
                                    updatedArticle.thumbnailPath = path;
                                  }
                                  
                                  return updatedArticle;
                                });
                              }}
                              showValidation={true}
                              allowExternalUrl={true}
                              placeholder={`Upload ${editArticle.postType === PostType.VIDEO_POST ? 'video' : editArticle.postType === PostType.PODCAST_POST ? 'audio' : editArticle.postType === PostType.IMAGE_POST ? 'image' : 'file'} content`}
                              size="sm"
                            />
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label>Thumbnail Image</label>
                        
                        {/* Checkbox for using same as main content */}
                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="useSameAsMainContentEdit"
                              checked={useSameAsMainContent}
                              onChange={handleUseSameAsMainContentChange}
                            />
                            <label className="form-check-label" htmlFor="useSameAsMainContentEdit">
                              Use same as main content/post image
                            </label>
                          </div>
                        </div>
                        
                        <div className="d-flex flex-column align-items-start">
                          {editArticle.thumbnailPath ? (
                            <div className="d-flex flex-column align-items-start">
                              <MediaRenderer
                                fileUrl={editArticle.thumbnailPath}
                                postType={PostType.IMAGE_POST} // Thumbnails are always images
                                alt="Thumbnail Image"
                                style={{ 
                                  maxWidth: '100%', 
                                  marginBottom: '10px',
                                  borderRadius: '4px'
                                }}
                                onError={(e) => {
                                  console.error('Failed to load thumbnail:', editArticle.thumbnailPath);
                                }}
                              />
                              <button
                                className="btn btn-danger btn-sm mt-2"
                                onClick={() => handleRemoveImage('thumbnailPath')}
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            !useSameAsMainContent && (
                              <EnhancedFileUpload
                                instanceId="view-article-thumbnail"
                                postType={PostType.IMAGE_POST}
                                fileCategory="thumbnail"
                                onFileUploaded={(path) => {
                                  setEditArticle(prev => {
                                    if (!prev) return prev;
                                    return { ...prev, thumbnailPath: path };
                                  });
                                }}
                                showValidation={true}
                                allowExternalUrl={true}
                                placeholder="Upload thumbnail image"
                                size="sm"
                              />
                            )
                          )}
                          
                          {useSameAsMainContent && !editArticle.thumbnailPath && !editArticle.imageUrl && (
                            <div className="text-muted">
                              <i className="fas fa-info-circle me-2"></i>
                              Upload main content first to use it as thumbnail
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* TTS Audio Preview Section */}
                      {editArticle.audioUrl && (
                        <div className="mb-3">
                          <label>Generated Audio (TTS)</label>
                          <div className="border rounded p-3 bg-light">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-volume-up text-success me-2" style={{ fontSize: '1.2rem' }}></i>
                              <span className="text-success fw-bold">Audio Generated Successfully</span>
                            </div>
                            <audio 
                              controls 
                              style={{ width: '100%' }}
                              src={editArticle.audioUrl}
                            >
                              Your browser does not support the audio element.
                            </audio>
                            <div className="mt-2 d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                <i className="fas fa-link me-1"></i>
                                {editArticle.audioUrl.split('/').pop()}
                              </small>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => setEditArticle(prev => prev ? { ...prev, audioUrl: undefined } : prev)}
                              >
                                <i className="fas fa-trash me-1"></i>
                                Remove Audio
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={() => setShowConfirmUpdate(true)}
                        disabled={saving}
                        style={{ minWidth: '9rem' }}
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            />
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save" />
                            Update Article
                          </>
                        )}
                      </button>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </Page>
          </div>

          {/* Right Column: Subarticles (narrower) */}
          <div className="col-md-5">
            <Page>
              <div className="row mb-4">
                <div className="col-12">
                  <Card className="mb-4">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">Subarticles</h4>
                        <button className="btn btn-primary" onClick={() => openNewSubModal()}>
                          Add Subarticle
                        </button>
                      </div>
                      {subArticles.length === 0 && (
                        <div className="text-muted text-center mb-3">No subarticles added yet.</div>
                      )}
                      <div className="row">
                        {subArticles.map((sub, index) => (
                          <div className="col-12 mb-3" key={sub.id}>
                            <Card>
                              <CardBody>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="fw-bold">{`${index + 1}. ${sub.title}`}</div>
                                    <div className="text-muted small">{sub.body.slice(0, 60)}...</div>
                                    <div className="d-flex align-items-center gap-3 mt-2">
                                      {sub.fileUrl && (
                                        <MediaRenderer
                                          fileUrl={sub.fileUrl}
                                          postType={sub.postType}
                                          alt="Subarticle Content"
                                          style={{ maxWidth: 50, maxHeight: 50 }}
                                          className="rounded"
                                          onError={(e) => {
                                            console.error('Failed to load subarticle content:', sub.fileUrl);
                                          }}
                                        />
                                      )}
                                      {sub.thumbnailPath && (
                                        <MediaRenderer
                                          fileUrl={sub.thumbnailPath}
                                          postType={PostType.IMAGE_POST} // Thumbnails are always images
                                          alt="Subarticle Thumbnail"
                                          style={{ maxWidth: 50, maxHeight: 50 }}
                                          className="rounded"
                                          onError={(e) => {
                                            console.error('Failed to load subarticle thumbnail:', sub.thumbnailPath);
                                          }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => openSubModal(sub)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleRemoveSubArticle(sub.id)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                                <div className="mt-3">
                                  {/* Removed full-size image and thumbnail content */}
                                </div>
                              </CardBody>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </Page>
          </div>
        </div>

        {/* Update Confirmation Modal */}
        <Modal
          isOpen={showConfirmUpdate}
          setIsOpen={setShowConfirmUpdate}
          size='sm'
          titleId='confirm-update-article-modal'
          isStaticBackdrop
          isScrollable={false}
          isCentered
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.75 }}
          transition={{ ease: 'easeOut', duration: 0.2 }}
        >
          <ModalHeader setIsOpen={setShowConfirmUpdate}>
            <ModalTitle id='confirm-update-article-modal'>
              <Icon icon='Edit' className='me-2 text-primary' />
              Update Article
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div className='d-flex align-items-start'>
              <div className='flex-shrink-0 me-3'>
                <div
                  className='rounded-circle d-flex align-items-center justify-content-center'
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(13, 110, 253, 0.1)',
                    border: '2px solid rgba(13, 110, 253, 0.2)',
                  }}
                >
                  <Icon icon='Save' className='text-primary' size='lg' />
                </div>
              </div>
              <div className='flex-grow-1'>
                <h6 className='mb-1 text-dark'>Confirm Update</h6>
                <p className='mb-0 text-muted'>
                  Are you sure you want to update{' '}
                  <strong className='text-dark'>"{editArticle?.title || 'this article'}"</strong>?
                  All changes will be saved.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color='light'
              onClick={() => setShowConfirmUpdate(false)}
              isDisable={saving}
            >
              No, Cancel
            </Button>
            <Button
              color='primary'
              className='d-inline-flex align-items-center gap-2'
              isDisable={saving}
              onClick={() => handleUpdate()}
            >
              {saving ? (
                <>
                  <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                  Updating...
                </>
              ) : (
                <>
                  <Icon icon='Check' />
                  Yes, Update
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {subModalOpen && subModalInitialData && (
          <SubArticleModal
            isOpen={subModalOpen}
            setIsOpen={setSubModalOpen}
            initialData={subModalInitialData}
            onSave={(updatedSub) => {
              setSubArticles((prev) => {
                if (updatedSub.id === 0 || updatedSub.id === null) {
                  // Ensure id is explicitly set to 0 or null for new subarticles
                  return [...prev, { ...updatedSub, id: 0 }];
                }
                return prev.map((s) => (s.id === updatedSub.id ? updatedSub : s));
              });
              closeSubModal();
            }}
            tagsList={[]}
            parentPostType={editArticle?.postType || undefined}
            enableAIAssistant={true}
          />
        )}
      </PageWrapper>
    </div>
  );
};

export default ViewArticle;
